import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { Dispatch, AnyAction } from 'redux';
import { mobileNavigation } from 'store/actions/user/navigations';
import NavBarIconMenu from 'components/Navigations/NavBarIconMenu/NavBarIconMenu';
import NavBarDropDown from 'components/Navigations/NavBarDropDown/NavBarDropDown';
import CircleButton from 'components/form/CircleButton';
import EnkiLogo from 'components/Image/EnkiLogo';
import EnkiDarkLogo from 'components/Image/EnkiDarkLogo';
import { Link } from 'react-router-dom';
import './NavBar.scss';

const loadLogo = (theme: string) => {
  switch (theme) {
    case 'transparent':
      return <EnkiDarkLogo />;
    case 'dark':
    case 'light':
    default:
      return <EnkiLogo />;
  }
};

export type NavIconMenuItem = Record<string, unknown>;

export type NavBarProps = {
  iconMenuItems?: NavIconMenuItem[];
  isDemo?: boolean;
  theme?: 'light' | 'dark' | 'transparent' | string;
  dropdownTheme?: string;
  withoutNavigation?: boolean;
  logo?: {
    small?: boolean;
    desktop?: boolean;
  };
  leftContent?: React.ReactNode;
};

// Props injected from Redux
type ReduxProps = {
  mobileNavigationStatus: boolean;
  mobileNavigation: (status: boolean) => void;
};

type Props = NavBarProps & ReduxProps;

const NavBar: React.FC<Props> = ({
  iconMenuItems,
  isDemo = false,
  theme = 'light',
  dropdownTheme = 'default',
  withoutNavigation = false,
  logo = { small: true, desktop: true },
  mobileNavigationStatus,
  mobileNavigation,
  leftContent,
}) => {
  const onNavigationButtonClick = useCallback(() => {
    mobileNavigation(!mobileNavigationStatus);
  }, [mobileNavigation, mobileNavigationStatus])  ;

  return (
    <div className="navbar" data-theme={theme} data-has-logo={withoutNavigation || !!(logo.small || logo.desktop)}>
      {!withoutNavigation && (logo.small || logo.desktop) && (
        <div className="navbar__brand" data-small={logo.small} data-desktop={logo.desktop}>
          {!isDemo && (
            <div className="navbar__brand__button">
              <CircleButton
                color="blue-grey-r"
                title={mobileNavigationStatus ? 'Hide menu' : 'Show menu'}
                icon={mobileNavigationStatus ? 'arrow_back' : 'reorder'}
                value="list"
                handleClick={onNavigationButtonClick}
              />
            </div>
          )}
          <div className="navbar__brand__logo">
            <Link to={`/`}>{loadLogo(theme)}</Link>
          </div>
        </div>
      )}
      {withoutNavigation && (
         <div className="navbar__brand hold">
           <div className="navbar__brand__logo">
             <Link to={`/`}>{loadLogo(theme)}</Link>
           </div>
         </div>
      )}
      <div className="navbar__body">
        <div className="navbar__body__left">
          {leftContent ? leftContent : <span />}
        </div>
        <div className="navbar__body__middle">
          <span />
        </div>
        <div className="navbar__body__right">
          {iconMenuItems ? <NavBarIconMenu menuItems={iconMenuItems} /> : <span />}
        </div>
      </div>
      <div className="navbar__end">{!isDemo ? <NavBarDropDown theme={dropdownTheme} /> : ''}</div>
    </div>
  );
};

// Minimal shape of the Redux store pieces we read
interface RootState {
  userReducers: {
    mobileNavigationReducers: {
      status: boolean;
    };
  };
}

const mapStateToProps = (store: RootState) => {
  const { userReducers } = store;
  const { mobileNavigationReducers } = userReducers;
  return {
    mobileNavigationStatus: mobileNavigationReducers.status,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => {
  return {
    mobileNavigation: (status: boolean) => dispatch(mobileNavigation(status)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(NavBar);
